import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, FileSpreadsheet, FileCode, FileType, Table2, Settings2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

const SCOPES = ["unsent", "week", "quarter", "year"];
const GROUPS = [
  { key: "acc_grp_documents", items: [
    { id: "word", icon: FileType, label: "Word .docx" },
    { id: "pdf", icon: FileText, label: "PDF" },
    { id: "text", icon: FileText, label: "fmt_text" },
  ] },
  { key: "acc_grp_spreadsheets", items: [
    { id: "xlsx", icon: FileSpreadsheet, label: "Excel .xlsx" },
    { id: "xls", icon: FileSpreadsheet, label: "Excel .xls" },
    { id: "csv", icon: Table2, label: "CSV" },
  ] },
  { key: "acc_grp_data", items: [
    { id: "xml", icon: FileCode, label: "XML" },
  ] },
];

// Boekhouding: handmatige export (download) van facturen & creditnota's in het gekozen formaat.
export default function BoekhoudingPanel() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [scope, setScope] = useState("unsent");
  const [sep, setSep] = useState(";");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState("");

  const loadPreview = async (s = scope) => {
    try { const { data } = await api.get(`/interfaces/accounting/preview`, { params: { scope: s } }); setCount(data.count); }
    catch (e) { setCount(null); toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadPreview(scope); /* eslint-disable-next-line */ }, [scope]);

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.post(`/interfaces/accounting/export`, null, {
        params: { scope, format, separator: sep }, responseType: "blob",
      });
      const dispo = res.headers["content-disposition"] || "";
      const name = /filename="?([^"]+)"?/.exec(dispo)?.[1] || `boekhouding.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(t("acc_export_done"));
      loadPreview(scope);
    } catch (e) {
      const d = e.response?.data;
      if (d instanceof Blob) { try { toast.error(apiError(JSON.parse(await d.text()).detail)); } catch { toast.error(t("acc_export_empty")); } }
      else toast.error(apiError(d?.detail));
    } finally { setBusy(""); }
  };

  const disabled = !!busy || !count;

  return (
    <div className="flex flex-col gap-4 max-w-[1200px]" data-testid="boekhouding-panel">
      {/* Kop met titel + link naar automatiseren */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Download className="h-5 w-5" />
          </span>
          <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">{t("acc_manual_export")}</h2>
        </div>
        {isAdmin && (
          <Button asChild variant="outline" size="sm" data-testid="acc-config-link">
            <Link to="/admin/interfaces/accounting"><Settings2 className="h-4 w-4 mr-1.5" />{t("acc_automate")}</Link>
          </Button>
        )}
      </div>

      {/* 3-staps bento: Selectie · Opties · Formaat */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 rounded-2xl border border-border bg-card p-6" data-testid="acc-export-panel">
        {/* Stap 1 — Selectie */}
        <div className="lg:col-span-4 flex flex-col gap-5">
          <div className="rounded-2xl border border-border bg-background p-4 flex flex-col gap-1" data-testid="acc-count">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_documents")}</span>
            <span className="font-mono text-3xl font-medium tracking-tight text-foreground leading-none">{count == null ? "…" : count}</span>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_selection")}</p>
              <button type="button" onClick={() => loadPreview(scope)} aria-label="refresh" data-testid="acc-refresh"
                className="text-muted-foreground hover:text-foreground transition-colors active:scale-90">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex flex-col gap-2" data-testid="acc-scope">
              {SCOPES.map((s) => {
                const on = scope === s;
                return (
                  <button key={s} type="button" onClick={() => setScope(s)} data-testid={`acc-scope-${s}`} aria-pressed={on}
                    className={`w-full text-left rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-all active:scale-[0.98] ${on
                      ? "border-primary bg-primary text-primary-foreground shadow-sm"
                      : "border-border bg-transparent text-foreground hover:bg-accent/50 hover:border-primary/40"}`}>
                    {t(`acc_scope_${s}`)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stap 2 — Opties */}
        <div className="lg:col-span-3 flex flex-col gap-3 lg:border-l lg:border-border lg:pl-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_csv_sep")}</p>
          <div className="space-y-2">
            <Input value={sep} onChange={(e) => setSep(e.target.value)} className="h-9 w-20 text-center font-mono" data-testid="acc-sep" aria-label={t("acc_csv_sep")} />
            <p className="text-xs leading-relaxed text-muted-foreground">{t("acc_csv_sep_hint")}</p>
          </div>
        </div>

        {/* Stap 3 — Formaat */}
        <div className="lg:col-span-5 flex flex-col gap-5 lg:border-l lg:border-border lg:pl-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("acc_format")}</p>
          <div className="flex flex-col gap-5" data-testid="acc-formats">
            {GROUPS.map((g) => (
              <div key={g.key} className="flex flex-col gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70">{t(g.key)}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {g.items.map((f) => {
                    const Icon = f.icon;
                    const loading = busy === f.id;
                    return (
                      <button key={f.id} type="button" disabled={disabled} onClick={() => download(f.id)} data-testid={`acc-download-${f.id}`}
                        aria-label={f.label.startsWith("fmt_") ? t(f.label) : f.label}
                        className="group relative flex items-center gap-3 rounded-xl border border-border bg-transparent p-3 transition-all hover:bg-accent/50 hover:border-primary/50 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none">
                        <Icon className="h-[18px] w-[18px] shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                        <span className="text-sm font-medium text-foreground">{f.label.startsWith("fmt_") ? t(f.label) : f.label}</span>
                        {loading
                          ? <span className="ml-auto text-xs text-primary">…</span>
                          : <Download className="ml-auto h-4 w-4 -translate-x-1.5 text-primary opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground px-1">{t("acc_export_note")}</p>
    </div>
  );
}
